import useBedbankDiscount from 'hooks/useBedbankDiscount'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { useContext, useMemo } from 'react'
import SearchTileWalledMessage from '../shared/SearchTileWalledMessage'
import BedbankSearchTileWalledContent from './BedbankSearchTileWalledContent'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import BedbankMetaSearchTileGarden from './BedbankMetaSearchTileGarden'
import OfferUrlContext from 'contexts/OfferUrlContext'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import useGhaVerifierPrice from 'hooks/useGhaVerifierPrice'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import useMetasearchPriceBreakdownCheck from 'hooks/Offers/useMetasearchPriceBreakdownCheck'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  hidePrice?: boolean
  hideIncludesTaxesAndFees?: boolean
  /** False on the secondary tiles, which are other hotels than the one Google priced. */
  isPrimaryMetaSearchResult?: boolean
}

function BedbankMetaSearchTilePriceStack({
  offer,
  filters = EmptyObject,
  hidePrice = false,
  hideIncludesTaxesAndFees = false,
  isPrimaryMetaSearchResult = false,
}: Props) {
  const {
    bundlePrice,
    bundlePropertyFees,
    bundleTaxesAndFees,
    discountPercentage,
    displayMargin,
    flightPrice,
    fetchingRates,
    hotelNights,
    hotelPrice,
    hotelRate,
    isFlexibleSearch,
    margin,
    marginAud,
    nightsToDisplay,
    offerPrice,
    promoteAsBundle,
    propertyFees,
    rooms,
    saleUnit,
    shownRate,
    suggestedDates,
    supplier,
    taxesAndFees,
    value,
  } = useBedbankOfferSearchPricing(offer, filters)

  const offerLocations: Array<string> = useMemo(() => {
    const locations = [
      offer.property?.address.city,
      offer.property?.address.stateProvinceName || '',
      offer.property?.address.countryName]
      .filter(Boolean)

    return locations
  }, [offer])

  const offerUrlFromContext = useContext(OfferUrlContext)
  const offerUrl = useMemo(() => offerUrlFromContext ?? getHotelOfferUrl(offer.id, offer.type), [offer, offerUrlFromContext])
  const [discountAmount, discountFetching, discountSettled] = useMetasearchDiscount(offerPrice, {
    propertyId: offer.id,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    rooms,
    disabled: promoteAsBundle,
  })
  const calculatedOfferPrice = Math.max(offerPrice - discountAmount, 0)
  const displayOfferPrice = useGhaVerifierPrice(calculatedOfferPrice, !isPrimaryMetaSearchResult || promoteAsBundle)
  const priceBreakdownDiscount = discountAmount > 0 && offerPrice > 0 ?
    discountAmount * hotelPrice / offerPrice :
    discountAmount

  useMetasearchPriceBreakdownCheck({
    ready: isPrimaryMetaSearchResult && !hidePrice && !promoteAsBundle && !!shownRate && !fetchingRates && !discountFetching && discountSettled,
    surface: 'metasearch',
    propertyId: offer.id,
    rateId: shownRate?.id,
    supplier: shownRate?.supplierCode,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    rooms,
    total: calculatedOfferPrice,
    taxesAndFees: taxesAndFees ?? 0,
    propertyFees: propertyFees ?? 0,
  })

  const discount = useBedbankDiscount(hotelRate?.discount, hotelRate?.memberDiscount)

  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })

  if (!hidePrice && !discountFetching && !isDisplayablePrice(displayOfferPrice)) return null

  return <BedbankSearchTileWalledContent
    wall={<SearchTileWalledMessage discountPercentage={discount} />}
    offer={offer}
    hidePricing={offer.hidePricing}
    filters={filters}
  >
    <BedbankMetaSearchTileGarden
      offer={offer}
      offerPrice={displayOfferPrice}
      isFlexibleSearch={isFlexibleSearch}
      suggestedDates={suggestedDates}
      nightsToDisplay={nightsToDisplay}
      promoteAsBundle={promoteAsBundle}
      saleUnit={saleUnit}
      rooms={rooms}
      discountPercentage={discountPercentage}
      value={value}
      hotelNights={hotelNights}
      offerLocations={offerLocations}
      displayMargin={displayMargin}
      margin={margin}
      marginAud={marginAud}
      supplier={supplier}
      directSearchPrices={directSearchPrices}
      shownRate={shownRate}
      bundlePropertyFees={bundlePropertyFees}
      propertyFees={propertyFees}
      bundleTaxesAndFees={bundleTaxesAndFees}
      taxesAndFees={taxesAndFees}
      bundlePrice={bundlePrice}
      hotelPrice={hotelPrice}
      postTaxDiscount={priceBreakdownDiscount}
      isGhaVerifiedRate={isPrimaryMetaSearchResult}
      flightPrice={flightPrice}
      offerUrl={offerUrl}
      hidePrice={hidePrice || discountFetching}
      hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
    />
  </BedbankSearchTileWalledContent>
}

export default BedbankMetaSearchTilePriceStack
